import React from 'react';
import { ArrowLeft, Newspaper, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/pageTranslations';

const Prensa = () => {
  const navigate = useNavigate();
  const lang = useLanguage();

  const texts = {
    es: {
      backTo: 'Volver a Dr. Norro',
      title: 'Artículos de Prensa',
      subtitle: 'El Dr. Norro en los medios',
      articulos: [
        {
          titulo: 'Rinomodelación: la alternativa sin cirugía para mejorar la nariz',
          medio: 'Revista de Medicina Estética',
          fecha: '2023',
          resumen: 'El Dr. Ángel Ruiz Norro explica las ventajas de la rinomodelación frente a la rinoplastia tradicional.'
        },
        {
          titulo: 'Técnicas avanzadas en bioplastia nasal',
          medio: 'Portal Salud',
          fecha: '2022',
          resumen: 'Artículo sobre las últimas técnicas en rinomodelación permanente desarrolladas en España.'
        },
        {
          titulo: 'Pioneros de la medicina estética en España',
          medio: 'Diario Sur',
          fecha: '2021',
          resumen: 'Reportaje sobre los profesionales que han revolucionado la medicina estética en nuestro país.'
        },
        {
          titulo: 'La rinomodelación gana terreno a la cirugía tradicional',
          medio: 'Revista Belleza y Salud',
          fecha: '2020',
          resumen: 'Análisis de las tendencias en tratamientos nasales no quirúrgicos.'
        }
      ],
      pressQuestion: '¿Eres periodista o medio de comunicación?',
      pressDesc: 'Para entrevistas o información de prensa, contacta con nosotros.',
      contact: 'Contactar',
    },
    en: {
      backTo: 'Back to Dr. Norro',
      title: 'Press Articles',
      subtitle: 'Dr. Norro in the media',
      articulos: [
        {
          titulo: 'Rhinomodeling: the non-surgical alternative to improve the nose',
          medio: 'Aesthetic Medicine Magazine',
          fecha: '2023',
          resumen: 'Dr. Ángel Ruiz Norro explains the advantages of rhinomodeling over traditional rhinoplasty.'
        },
        {
          titulo: 'Advanced techniques in nasal bioplasty',
          medio: 'Health Portal',
          fecha: '2022',
          resumen: 'Article about the latest techniques in permanent rhinomodeling developed in Spain.'
        },
        {
          titulo: 'Pioneers of aesthetic medicine in Spain',
          medio: 'Diario Sur',
          fecha: '2021',
          resumen: 'Report on the professionals who have revolutionized aesthetic medicine in our country.'
        },
        {
          titulo: 'Rhinomodeling gains ground over traditional surgery',
          medio: 'Beauty and Health Magazine',
          fecha: '2020',
          resumen: 'Analysis of trends in non-surgical nasal treatments.'
        }
      ],
      pressQuestion: 'Are you a journalist or media outlet?',
      pressDesc: 'For interviews or press information, contact us.',
      contact: 'Contact',
    },
    fr: {
      backTo: 'Retour à Dr. Norro',
      title: 'Articles de Presse',
      subtitle: 'Le Dr. Norro dans les médias',
      articulos: [
        {
          titulo: 'Rhinomodelage: l\'alternative non chirurgicale pour améliorer le nez',
          medio: 'Revue de Médecine Esthétique',
          fecha: '2023',
          resumen: 'Le Dr. Ángel Ruiz Norro explique les avantages du rhinomodelage par rapport à la rhinoplastie traditionnelle.'
        },
        {
          titulo: 'Techniques avancées en bioplastie nasale',
          medio: 'Portail Santé',
          fecha: '2022',
          resumen: 'Article sur les dernières techniques de rhinomodelage permanent développées en Espagne.'
        },
        {
          titulo: 'Pionniers de la médecine esthétique en Espagne',
          medio: 'Diario Sur',
          fecha: '2021',
          resumen: 'Reportage sur les professionnels qui ont révolutionné la médecine esthétique dans notre pays.'
        },
        {
          titulo: 'Le rhinomodelage gagne du terrain sur la chirurgie traditionnelle',
          medio: 'Magazine Beauté et Santé',
          fecha: '2020',
          resumen: 'Analyse des tendances en matière de traitements nasaux non chirurgicaux.'
        }
      ],
      pressQuestion: 'Êtes-vous journaliste ou média?',
      pressDesc: 'Pour des interviews ou des informations de presse, contactez-nous.',
      contact: 'Contacter',
    },
    pt: {
      backTo: 'Voltar para Dr. Norro',
      title: 'Artigos de Imprensa',
      subtitle: 'O Dr. Norro na mídia',
      articulos: [
        {
          titulo: 'Rinomodelação: a alternativa sem cirurgia para melhorar o nariz',
          medio: 'Revista de Medicina Estética',
          fecha: '2023',
          resumen: 'O Dr. Ángel Ruiz Norro explica as vantagens da rinomodelação em relação à rinoplastia tradicional.'
        },
        {
          titulo: 'Técnicas avançadas em bioplastia nasal',
          medio: 'Portal Saúde',
          fecha: '2022',
          resumen: 'Artigo sobre as últimas técnicas em rinomodelação permanente desenvolvidas na Espanha.'
        },
        {
          titulo: 'Pioneiros da medicina estética na Espanha',
          medio: 'Diario Sur',
          fecha: '2021',
          resumen: 'Reportagem sobre os profissionais que revolucionaram a medicina estética em nosso país.'
        },
        {
          titulo: 'A rinomodelação ganha terreno sobre a cirurgia tradicional',
          medio: 'Revista Beleza e Saúde',
          fecha: '2020',
          resumen: 'Análise das tendências em tratamentos nasais não cirúrgicos.'
        }
      ],
      pressQuestion: 'Você é jornalista ou meio de comunicação?',
      pressDesc: 'Para entrevistas ou informações de imprensa, entre em contato conosco.',
      contact: 'Contato',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion/doctor')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.backTo}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          
          <div className="space-y-6 mb-12">
            {t.articulos.map((articulo, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all border border-gray-100 rounded-none">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-none flex items-center justify-center flex-shrink-0">
                    <Newspaper className="text-gold" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[#1C1C1A] mb-2">
                      {articulo.titulo}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-text-muted mb-3">
                      <span>{articulo.medio}</span>
                      <span>•</span>
                      <span>{articulo.fecha}</span>
                    </div>
                    <p className="text-text-secondary">
                      {articulo.resumen}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="bg-cream p-8 rounded-none text-center">
            <h3 className="text-xl font-light text-[#1C1C1A] mb-4">
              {t.pressQuestion}
            </h3>
            <p className="text-text-secondary mb-6">
              {t.pressDesc}
            </p>
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-gold hover:bg-gold text-white"
            >
              {t.contact}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Prensa;
